import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import DatePicker from "../datepicker/DatePickerWithText";
import { showToast } from "../../utils/utils";
import privateAxios from "../../utils/axiosPrivate";
import moment from "moment";
import useGA4EventAction from "../../config/GoogleAnalytics/useGA4EventAction";
import useMixpanelEventAction from "../../config/mixpanel/useMixpanelEventAction";
import BulkUploadErrorModal from "./BulkUploadErrorModal";
import {
  commonValidationMsg,
  fundedInvoicesData,
  responseStatus,
  commonErrMsg,
  processing,
  buttonName,
  dashboard,
  metaPixel
} from "../../locales/en";
import Style from "./modal.module.scss";
import {
  FILE_SIZE_LIMIT,
  YYYYMMDD,
  TRACK_CUSTOM
} from "../../utils/constants/appConstants";
import { useSelector, useDispatch } from "react-redux";
import { getProfile } from "../../redux/userManagementSlice";
import {
  getDownloadTemplateInvoiceUrl,
  getuploadInvoiceUrl,
  getUploadMultipleInvoiceUrl
} from "../../utils/constants/apiEndPoints";
import { sanitizeInput } from "../../utils/utils";
import CommonModal from "./CommonModal";
import { INVOICE } from "../../route/paths";

const ManualUploadInv = ({
  isOpen,
  toggle,
  loadInvoceData,
  isUploadInvoices,
  gotoPage,
  setPageSize,
  multiFormSubmit
}) => {
  const [invoiceDate, setInvoiceDate] = useState();
  const [dueDate, setDueDate] = useState();
  const { manualInvoiceUploadGA4Event } = useGA4EventAction();
  const { manualInvoiceUploadMixpanelEvent } = useMixpanelEventAction();

  const [invalidModalData, setInvalidModalData] = useState([]);
  const [errorModal, setErrorModal] = useState(false);
  const [errorStatus, setErrorStatus] = useState(null);
  const [errorModalData, setErrorModalData] = useState([]);
  const dispatch = useDispatch();

  const initialLoading = {
    singleUploadBtn: false,
    multiUploadBtn: false
  };
  const [loading, setLoading] = useState(initialLoading);

  const profile = useSelector((state) => state.userManagement.profile);
  const legalName = profile?.userAccountMeta?.legalName || "";
  const navigate = useNavigate();

  const initialFormData = {
    legalName: "",
    multiUploadLegalName: "",
    invoiceNumber: "",
    debatorName: "",
    totalAmount: "",
    file: null,
    csvfile: null
  };
  const [formData, setFormData] = useState(initialFormData);

  const initialErrors = {
    legalNameError: "",
    multiUploadLegalNameError: "",
    invoiceNumberError: "",
    debatorNameError: "",
    totalAmountError: "",
    fileError: "",
    filesError: "",
    invoiceDateError: "",
    dueDateError: ""
  };
  const [errors, setErrors] = useState(initialErrors);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeInput(value);
    setFormData({ ...formData, [name]: sanitizedValue });
    setErrors({ ...errors, [`${name}Error`]: "" });
  };

  const handleIssueDateChange = (newDate) => {
    if (newDate) {
      const formattedIssueDate = moment(newDate).format(YYYYMMDD);

      setInvoiceDate(formattedIssueDate);

      if (dueDate && moment(formattedIssueDate).isSameOrAfter(dueDate)) {
        setErrors({
          ...errors,
          invoiceDateError: `${commonValidationMsg?.dateChange}`
        });
      } else {
        setErrors({ ...errors, invoiceDateError: "" });
      }
    } else {
      setInvoiceDate("");
    }
  };

  const handleDueDateChange = (newDate) => {
    if (newDate) {
      const formattedDueDate = moment(newDate).format(YYYYMMDD);
      setDueDate(formattedDueDate);

      if (invoiceDate && moment(invoiceDate).isSameOrAfter(formattedDueDate)) {
        setErrors({
          ...errors,
          dueDateError: `${commonValidationMsg?.dateChange}`
        });
      } else {
        setErrors({ ...errors, dueDateError: "" });
      }
    } else {
      setDueDate("");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size > FILE_SIZE_LIMIT) {
      setErrors({
        ...errors,
        fileError: `${commonValidationMsg?.fileLimitError}`
      });
      return;
    }
    setFormData({ ...formData, file });
    setErrors({ ...errors, fileError: "" });
  };

  const handleCsvFileChange = (e) => {
    const csvfile = e.target.files[0];
    if (csvfile && csvfile.size > FILE_SIZE_LIMIT) {
      setErrors({
        ...errors,
        filesError: `${commonValidationMsg?.fileLimitError}`
      });
      return;
    }
    setFormData({ ...formData, csvfile });
    setErrors({ ...errors, filesError: "" });
  };

  const validateForm = () => {
    const newErrors = {};
    let hasError = false;

    if (
      !formData.legalName &&
      (legalName === "" || legalName === null || legalName === undefined)
    ) {
      newErrors.legalNameError = `${commonValidationMsg?.legalName}`;
      hasError = true;
    }

    if (!formData.invoiceNumber) {
      newErrors.invoiceNumberError = `${commonValidationMsg?.invNumber}`;
      hasError = true;
    }

    if (!formData.debatorName) {
      newErrors.debatorNameError = `${commonValidationMsg?.debtorName}`;
      hasError = true;
    }

    if (!formData.totalAmount || isNaN(formData.totalAmount)) {
      newErrors.totalAmountError = `${commonValidationMsg?.invTotal}`;
      hasError = true;
    }

    if (!formData.file) {
      newErrors.fileError = `${commonValidationMsg?.invcopy}`;
      hasError = true;
    }

    if (!invoiceDate) {
      newErrors.invoiceDateError = `${commonValidationMsg?.issueDate}`;
      hasError = true;
    }

    if (!dueDate) {
      newErrors.dueDateError = `${commonValidationMsg?.dueDate}`;
      hasError = true;
    } else if (moment(invoiceDate).isSame(dueDate)) {
      newErrors.invoiceDateError = `${commonValidationMsg?.issueDateSame}`;
      hasError = true;
    } else if (moment(invoiceDate).isAfter(dueDate)) {
      newErrors.invoiceDateError = `${commonValidationMsg?.issueDateSame}`;
      hasError = true;
    }

    setErrors(newErrors);
    return !hasError;
  };

  const handleFormSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading((prev) => ({ ...prev, singleUploadBtn: true }));
      const formDataToSend = new FormData();

      const documentPayload = {
        legalName: sanitizeInput(formData.legalName || ""),
        invoiceNumber: sanitizeInput(formData.invoiceNumber || ""),
        debatorName: sanitizeInput(formData.debatorName || ""),
        totalAmount: parseFloat(formData.totalAmount) || 0,
        dueDate: dueDate,
        invoiceDate: invoiceDate
      };

      formDataToSend.append(
        "invoiceDto",
        new Blob([JSON.stringify(documentPayload)], {
          type: "application/json"
        })
      );

      // Append file
      formDataToSend.append("file", formData.file);

      const config = {
        method: "post",
        url: getuploadInvoiceUrl,
        headers: { "Content-Type": "multipart/form-data" },
        data: formDataToSend
      };

      const apiResponse = await privateAxios(config);

      if (apiResponse.status === 200 || apiResponse.status === 201) {
        manualInvoiceUploadMixpanelEvent(apiResponse?.data?.data);
        manualInvoiceUploadGA4Event(apiResponse?.data);

        if (window.fbq) {
          window.fbq(TRACK_CUSTOM, metaPixel.submitInvoice);
        }
        if (isUploadInvoices) {
          navigate(INVOICE);
        } else {
          loadInvoceData();
          gotoPage(0);
          setPageSize(10);
        }
        showToast(
          fundedInvoicesData?.statusMessage?.msg2,
          responseStatus?.success
        );
        setFormData({
          legalName: "",
          invoiceNumber: "",
          debatorName: "",
          totalAmount: "",
          file: null
        });
        setDueDate("");
        setInvoiceDate("");
        toggle();
        dispatch(getProfile());
      }
    } catch (error) {
      // Extract error message
      const message =
        error.response?.data?.message || error.message || commonErrMsg?.item2;
      showToast(message, responseStatus?.error);
    } finally {
      setLoading((prev) => ({ ...prev, singleUploadBtn: false }));
    }
  };

  const validateMultiUploadForm = () => {
    let hasError = false;
    const newErrors = {};

    if (
      !formData.multiUploadLegalName &&
      (legalName === "" || legalName === null || legalName === undefined)
    ) {
      newErrors.multiUploadLegalNameError = `${fundedInvoicesData?.statusMessage?.msg12}`;
      hasError = true;
    }

    if (!formData.csvfile) {
      newErrors.filesError = `${commonValidationMsg?.csvFile}`;
      hasError = true;
    }

    setErrors(newErrors);
    return !hasError;
  };

  const handleDownloadCsv = async () => {
    try {
      const response = await privateAxios.get(getDownloadTemplateInvoiceUrl, {
        responseType: "blob"
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "invoices.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      const message = error.response?.data?.message || commonErrMsg?.item2;
      showToast(message, responseStatus?.error);
    }
  };

  const handleErrorModal = () => {
    setErrorModal(!errorModal);
    navigate(INVOICE);
  };
  const parseNewErrorMessage = (message) => {
    const regex =
      /Invalid issue date format for invoice number '([^']+)'\. Provided issue date: '([^']+)'\. Expected format: ([^.\\n]+)\./g;
    const results = [];
    let match;

    while ((match = regex.exec(message)) !== null) {
      results.push({
        invoiceNumber: match[1],
        issueDate: match[2],
        expectedFormat: match[3]
      });
    }

    return results;
  };

  const handleMultiFormSubmit = async (e) => {
    if (!isUploadInvoices && e) {
      e.preventDefault();
    }

    if (!validateMultiUploadForm()) return;

    const formDataToSend = new FormData();
    const documentPayload = {
      legalName: sanitizeInput(formData.multiUploadLegalName || "")
    };

    formDataToSend.append(
      "invoiceDto",
      new Blob([JSON.stringify(documentPayload)], { type: "application/json" })
    );
    formDataToSend.append("csvFile", formData.csvfile);

    if (isUploadInvoices) {
      try {
        setLoading((prev) => ({ ...prev, multiUploadBtn: true }));

        const config = {
          method: "post",
          url: getUploadMultipleInvoiceUrl,
          headers: { "Content-Type": "multipart/form-data" },
          data: formDataToSend
        };

        const apiResponse = await privateAxios(config);
        if (apiResponse.status === 200 || apiResponse.status === 201) {
          const responseData = apiResponse?.data?.data;

          if (window.fbq) {
            window.fbq(TRACK_CUSTOM, metaPixel.submitMultipleInvoices);
          }
          if (responseData?.invalidInvoicesCount > 0) {
            setInvalidModalData(responseData?.invalidInvoices);
            setErrorStatus(apiResponse?.status);
            setErrorModal(true);
          } else {
            navigate(INVOICE);
          }

          if (responseData?.invalidInvoicesCount === 0) {
            showToast(
              fundedInvoicesData?.statusMessage?.msg2,
              responseStatus?.success
            );
          }
          setFormData({ multiUploadLegalName: "", csvfile: null });
          dispatch(getProfile());
          toggle();
        }
      } catch (error) {
        const errorMessage = error.response?.data?.message || "";
        if (error.response?.status === 500) {
          if (
            errorMessage.includes(fundedInvoicesData?.statusMessage?.msg3) ||
            errorMessage.includes(fundedInvoicesData?.statusMessage?.msg4) ||
            errorMessage.includes(fundedInvoicesData?.statusMessage?.msg5) ||
            errorMessage.includes(fundedInvoicesData?.statusMessage?.msg6) ||
            errorMessage.includes(fundedInvoicesData?.statusMessage?.msg7) ||
            errorMessage.includes(fundedInvoicesData?.statusMessage?.msg8)
          ) {
            showToast(
              fundedInvoicesData?.statusMessage?.msg9,
              responseStatus?.error
            );
          } else {
            const parsedData = parseNewErrorMessage(errorMessage);
            setErrorModalData(parsedData);
            setErrorStatus(error.response?.status);
            toggle();
            setErrorModal(true);
            setFormData({ multiUploadLegalName: "", csvfile: null });
          }
        } else {
          const message =
            error.response?.data?.message ||
            fundedInvoicesData?.statusMessage?.msg10;
          showToast(message, responseStatus?.error);
        }
      } finally {
        setLoading((prev) => ({ ...prev, multiUploadBtn: false }));
      }
    } else {
      setLoading((prev) => ({ ...prev, multiUploadBtn: true }));
      try {
        const result = multiFormSubmit(formDataToSend);
        await Promise.resolve(result);
        if (window.fbq) {
          window.fbq(TRACK_CUSTOM, metaPixel.submitMultipleInvoices);
        }
      } catch (err) {
        const message = err?.message || commonErrMsg?.item2;
        showToast(message, responseStatus?.error);
      } finally {
        setLoading((prev) => ({ ...prev, multiUploadBtn: false }));
      }
    }
  };

  const handleCloseModal = () => {
    setErrors({
      legalNameError: "",
      multiUploadLegalNameError: "",
      invoiceNumberError: "",
      debatorNameError: "",
      totalAmountError: "",
      fileError: "",
      filesError: "",
      invoiceDateError: "",
      dueDateError: ""
    });

    setFormData({
      legalName: "",
      multiUploadLegalName: "",
      invoiceNumber: "",
      debatorName: "",
      totalAmount: "",
      file: null,
      csvfile: null
    });

    setInvoiceDate("");
    setDueDate("");
    toggle();
  };

  return (
    <>
      <CommonModal
        isOpen={isOpen}
        toggle={handleCloseModal}
        title=" "
        modalClassName={`${Style.manualUploadModal} modal-xl`}
        headerClassName={`${Style.creditModalHeader} text-hide`}
        bodyClassName={`${Style.manualUploadContent} modal-content border-0`}
      >
        <div className={`${Style.manualUploadContent} modal-content border-0`}>
          <div className="inv-upload-template d-flex flex-wrap-reverse">
            <div className={`${Style.manul_Invoice_Submit} ${Style.width_45}`}>
              <h2>{fundedInvoicesData?.manualUploadModal?.title}</h2>
              <p>{fundedInvoicesData?.manualUploadModal?.subTitle}</p>
              <br />
              <div className="multiple-inv">
                <div className={Style.formGroup}>
                  <button
                    className="btn btn-primary"
                    onClick={handleDownloadCsv}
                  >
                    {
                      fundedInvoicesData?.manualUploadModal?.btnGroup
                        ?.downloadTamplate
                    }
                  </button>
                </div>
                {(legalName === null ||
                  legalName === undefined ||
                  legalName === "") && (
                  <div className={Style.formGroup}>
                    <input
                      type="text"
                      placeholder={
                        fundedInvoicesData?.manualUploadModal?.businessLegal
                      }
                      name="multiUploadLegalName"
                      value={formData.multiUploadLegalName}
                      onChange={handleInputChange}
                    />
                    {errors.multiUploadLegalNameError && (
                      <div className="text-danger small mt-1">
                        {errors.multiUploadLegalNameError}
                      </div>
                    )}
                  </div>
                )}

                <div className={Style.formGroup}>
                  <input
                    type="file"
                    name="file"
                    accept=".csv"
                    onChange={handleCsvFileChange}
                  />
                  {errors.filesError && (
                    <div className="text-danger small mt-1">
                      {errors.filesError}
                    </div>
                  )}
                </div>
                <div className={Style.formGroup}>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    onClick={handleMultiFormSubmit}
                    disabled={loading.multiUploadBtn}
                  >
                    {loading.multiUploadBtn
                      ? processing
                      : `${buttonName.submit}`}
                  </button>
                </div>
              </div>
            </div>
            <div className={`${Style.manul_Invoice_Submit} ${Style.width_10}`}>
              <strong>{fundedInvoicesData?.manualUploadModal?.or}</strong>
            </div>
            <div
              className={`fix_center ${Style.manul_Invoice_Submit} ${Style.width_45}`}
            >
              <h2>{fundedInvoicesData?.manualUploadModal?.titleSingleInv}</h2>
              <br />
              <div className="single-inv">
                {(legalName === null ||
                  legalName === undefined ||
                  legalName === "") && (
                  <div className={Style.formGroup}>
                    <input
                      type="text"
                      placeholder={
                        fundedInvoicesData?.manualUploadModal?.businessLegal
                      }
                      name="legalName"
                      value={formData.legalName}
                      onChange={handleInputChange}
                    />
                    {errors.legalNameError && (
                      <div className="text-danger small mt-1">
                        {errors.legalNameError}
                      </div>
                    )}
                  </div>
                )}

                <div className={Style.formGroup}>
                  <input
                    type="text"
                    placeholder={
                      fundedInvoicesData?.manualUploadModal?.invNumber
                    }
                    name="invoiceNumber"
                    value={formData.invoiceNumber}
                    onChange={handleInputChange}
                  />
                  {errors.invoiceNumberError && (
                    <div className="text-danger small mt-1">
                      {errors.invoiceNumberError}
                    </div>
                  )}
                </div>
                <div className={Style.formGroup}>
                  <input
                    type="text"
                    placeholder={fundedInvoicesData?.manualUploadModal?.debtor}
                    name="debatorName"
                    value={formData.debatorName}
                    onChange={handleInputChange}
                  />
                  {errors.debatorNameError && (
                    <div className="text-danger small mt-1">
                      {errors.debatorNameError}
                    </div>
                  )}
                </div>
                <div className={`${Style.formGroup} ${Style.gray_bg}`}>
                  <DatePicker
                    placeholder={
                      fundedInvoicesData?.manualUploadModal?.issueDate
                    }
                    value={invoiceDate}
                    onChange={handleIssueDateChange}
                  />
                  {errors.invoiceDateError && (
                    <div className="text-danger small mt-1">
                      {errors.invoiceDateError}
                    </div>
                  )}
                </div>
                <div className={`${Style.formGroup} ${Style.gray_bg}`}>
                  <DatePicker
                    placeholder={dashboard?.invoiceDatatable.dueDate}
                    value={dueDate}
                    onChange={handleDueDateChange}
                    minDate={new Date()}
                  />
                  {errors.dueDateError && (
                    <div className="text-danger small mt-1">
                      {errors.dueDateError}
                    </div>
                  )}
                </div>
                <div className={Style.formGroup}>
                  <input
                    type="text"
                    placeholder={
                      fundedInvoicesData?.manualUploadModal?.invTotal
                    }
                    name="totalAmount"
                    value={formData.totalAmount}
                    onChange={handleInputChange}
                  />
                  {errors.totalAmountError && (
                    <div className="text-danger small mt-1">
                      {errors.totalAmountError}
                    </div>
                  )}
                </div>
                <div className={Style.formGroup}>
                  <label htmlFor="">
                    {fundedInvoicesData?.manualUploadModal?.uploadInv}
                  </label>
                  <input
                    type="file"
                    name="file"
                    accept="application/pdf,image/*"
                    onChange={handleFileChange}
                  />
                  {errors.fileError && (
                    <div className="text-danger small mt-1">
                      {errors.fileError}
                    </div>
                  )}
                </div>
                <div className={Style.formGroup}>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    onClick={handleFormSubmit}
                    disabled={loading.singleUploadBtn}
                  >
                    {loading.singleUploadBtn
                      ? processing
                      : `${buttonName.submit}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CommonModal>

      {isUploadInvoices && (
        <BulkUploadErrorModal
          isOpen={errorModal}
          toggle={handleErrorModal}
          invalidData={invalidModalData}
          errorModalData={errorModalData}
          errorStatus={errorStatus}
        />
      )}
    </>
  );
};

ManualUploadInv.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  loadInvoceData: PropTypes.any,
  isUploadInvoices: PropTypes.any,
  gotoPage: PropTypes.any,
  setPageSize: PropTypes.any,
  multiFormSubmit: PropTypes.any
};
export default ManualUploadInv;
